import { useEffect, useRef ,useState} from "react";
import Choices from "choices.js";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateGradient,
  clone,
  fetchCountryMappingForColor,
  fetchDisaggregationList,
  formatCountriesForQueryParams,
  formatFiltersForDataAPI,
  formatPeriodicityValues,
  getAPIHeaders,
  getDataByDimension,
  getEndpointFromConfig,
  getMappingForBenchmark,
  getUnitMeasureItems,
  isCountryInGroup,
  isMultipleFrequencyExist,
  isMultipleUnitMeasureExist,
  getMaxYearFromDimensions,
} from "../common/utils/helper";
import ComparisonByDisaggregation from "./ComparisonByDisaggregation";
import {
  setFilteredIndicatorData,
  setIndicatorData,
  setIsLoading,
  setSecondIndicatorData,
  setSelectedCountries,
  setSelectedMaxYear,
  setSelectedMinYear,
  setSelectedSecondIndicator,
  setRecentYearSelection,
  setSelectedSingleYear,
  setSelectedCountriesForBar,
  setSelectedComparison,
  setInitialFilters,
  setSelectedFilters,
  setSelectedUnitMeasure,
  resetSelectedCountries,
  setSelectedOnlyShow,
  setIndicatorMinYear,
  setIndicatorMaxYear,
  setSelectedFrequency,
  setSecondDisaggregationData,
  setSelectedSecondUnitMeasure,
  resetSelectedAverage,
  setDefaultSecondUnitMeasure,
  setIsSecondIndicatorLoading,
  setUnitMeasureName,
  setSelectedAverage,
  setSelectedAverages,
  setDimensionsData,
  setIsDimensionsLoading,
  setSelectedDimensionsList,
  clearSelectedDimensionsList,
  replaceSelectedDimensionsList,
  removeFromSelectedDimensionsList,
} from "./indicatorProfileSlice";
import {
  QueryParams,
  chartTypes,
  customReportFilterParams,
  endpointLabels,
  periodicity,
  mapTypes,
  aggregates,
  disaggregations,
} from "../common/utils/enum";
import {
  API_SUCCESS_CODE,
  CORRELATION_NONE_INDICATOR_VALUE,
  NONE,
  ONLY_SHOW_DEFAULT,
  TOTAL,
  WORLD_DEFAULT,
} from "../common/utils/constants";
import {
  fetchAndSetData,
  fetchAndSetPostMethodData,
} from "../common/commonSlice";
import fetchData from "../service/apiService";
import AverageDropdown from "./AverageDropdown";
import BoundaryTypeSelector from "./BoundaryTypeSelector";
import SecondIndicatorSelection from "./SecondIndicatorSelection";
import useQueryParams from "../common/hooks/useQueryParams";
import { COLOR_MAP } from "../common/utils/theme";
import OnlyShowDropdown from "./OnlyShowDropdown";

const Filters = ({ chartType }) => {
  const selectCountriesRef = useRef(null);

  const [singleRangeYearList, setSingleRangeYearList] = useState([]);
  const [rangeYearList, setRangeYearList] = useState([]);
  const [selectedMinDateIndex, setSelectedMinDateIndex] = useState(0);
  const [selectedMaxDateIndex, setSelectedMaxDateIndex] = useState(0);
  const [dateList, setDateList] = useState([]);
  const {
    indicatorMetadata,
    disaggregationCountries,
    indicatorCode,
    selectedCountries,
    selectedCountriesForBar,
    indicatorMinYear,
    indicatorMaxYear,
    selectedMinYear,
    selectedMaxYear,
    recentYearSelection,
    selectedSingleYear,
    indicatorData,
    selectedComparison,
    filteredIndicatorData,
    selectedFilters,
    selectedSecondIndicator,
    disaggregationData,
    secondDisaggregationData,
    selectedUnitMeasure,
    selectedSecondUnitMeasure,
    selectedFrequency,
    selectedOnlyShow,
    defaultFrequency,
    defaultUnitMeasure,
    defaultSecondUnitMeasure,
    isSelectedFiltersUpdated,
    dimensionsData,
    changedFilterName,
    selectedDimensionsList,
    boundaryType,
    availableBoundaryTypes,
    selectedAverages,
    countryHierarchy,
    countryListForIndicator,
    isCountryListLoaded,
  } = useSelector((state) => state.indicatorProfile);
  const { endpointList, grouping, featureProps, apiKeyName, apiKeyValue } =
    useSelector((state) => state.common);
  const { isInternalPage, secondIndicator, site } = featureProps;
  const headers = getAPIHeaders({
    isInternalPage,
    apiKeyName,
    apiKeyValue,
  });
  const isSingleSelectionGrouping =
    indicatorMetadata?.series_description?.database_id === "IFC_GB" ||
    chartType === chartTypes.StackedBar ||
    chartType === chartTypes.Pie;
  const regionCountriesMapping = grouping["HCL_WB_REGIONS"]
    ? grouping["HCL_WB_REGIONS"]
    : [];
  const dispatch = useDispatch();
  const { setQueryParam, deleteQueryParams, getQueryParam } = useQueryParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debounceTimer = useRef(null);
  const dimensionsDebounceTimer = useRef(null);
  const isInitialDimensionsLoad = useRef(true);
  useEffect(() => {
    if (chartType === chartTypes.Bar || chartType === chartTypes.DataTable) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchIndicatorsData();
      }, 300);
    }
  }, [selectedMinYear, selectedMaxYear]);

  useEffect(() => {
    calculateGradient();
  }, [
    selectedMinYear,
    selectedMaxYear,
    indicatorMinYear,
    indicatorMaxYear,
    rangeYearList,
  ]);

  useEffect(() => {
    dispatch(setIsLoading(true));
    if (
      indicatorCode &&
      (chartType === chartTypes.Trend || chartType === chartTypes.StackedBar) &&
      isCountryListLoaded &&
      isSelectedFiltersUpdated
    ) {
      selectedCountries.length > 0 &&
      disaggregationData?.dimensions &&
      disaggregationData.dimensions.length > 0
        ? fetchIndicatorsData()
        : dispatch(setIsLoading(false));
    } else if (
      indicatorCode &&
      chartType === chartTypes.Bar &&
      isCountryListLoaded &&
      isSelectedFiltersUpdated
    ) {
      selectedCountriesForBar.length > 0 &&
      disaggregationData?.dimensions &&
      disaggregationData.dimensions.length > 0
        ? fetchIndicatorsData()
        : dispatch(setIsLoading(false));
    } else if (
      [
        chartTypes.Map,
        chartTypes.Correlation,
        chartTypes.DataTable,
        chartTypes.Pie,
      ].includes(chartType) &&
      isCountryListLoaded &&
      isSelectedFiltersUpdated
    ) {
      disaggregationData?.dimensions.length > 0
        ? fetchIndicatorsData()
        : dispatch(setIsLoading(false));
    }
  }, [
    indicatorCode,
    selectedCountries,
    selectedCountriesForBar,
    chartType,
    selectedFilters,
    selectedFrequency,
    isCountryListLoaded,
    disaggregationData,
    isSelectedFiltersUpdated,
    recentYearSelection,
  ]);

  useEffect(() => {
    if (
      indicatorCode &&
      (chartType === chartTypes.Map ||
        chartType === chartTypes.Correlation ||
        chartType === chartTypes.Bar ||
        chartType === chartTypes.DataTable ||
        chartType === chartTypes.Pie ||
        ((chartType === chartTypes.Trend || chartType === chartTypes.Stack) &&
          selectedFrequency === periodicity.Daily &&
          selectedCountries?.length > 0 &&
          dateList?.length > 0)) &&
      isSelectedFiltersUpdated
    ) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchIndicatorsData();
      }, 300);
    }
  }, [selectedSingleYear, selectedMinDateIndex, selectedMaxDateIndex]);

  useEffect(() => {
    const fetchSecondIndicatorMetadataAndDimensions = async () => {
      try {
        dispatch(setIsSecondIndicatorLoading(true));

        const secIndicatorQueryParam = getQueryParam(
          QueryParams.SecondIndicator,
        );

        let secondIndicatorId = "";
        if (
          selectedSecondIndicator?.id &&
          selectedSecondIndicator.id !== CORRELATION_NONE_INDICATOR_VALUE?.id
        ) {
          secondIndicatorId = selectedSecondIndicator.id;
        } else if (
          secIndicatorQueryParam &&
          secIndicatorQueryParam.toLowerCase() !==
            secondIndicator.toLowerCase() &&
          secIndicatorQueryParam !== CORRELATION_NONE_INDICATOR_VALUE?.id
        ) {
          secondIndicatorId = secIndicatorQueryParam;
        } else if (
          secIndicatorQueryParam === CORRELATION_NONE_INDICATOR_VALUE?.id
        ) {
          secondIndicatorId = "";
        } else {
          secondIndicatorId = secondIndicator;
        }

        if (secondIndicatorId) {
          let datasetID = selectedSecondIndicator?.datasetID;

          const secondIndicatorHeaders = getAPIHeaders({
            apiKeyName,
            apiKeyValue,
          });
          const adminMetadataEndpoint = getEndpointFromConfig(
            endpointList,
            endpointLabels.AdminMetadata,
          ).apiEndpoint;
          const adminMetadataPayload = {
            ids: [secondIndicatorId],
            type: "indicator",
          };
          const getEntitiesByIdEndpoint = getEndpointFromConfig(
            endpointList,
            endpointLabels.GetEntitiesById,
          ).apiEndpoint;

          const getEntitiesByIdPayload = {
            site,
            data_classification: ["public"],
            entity_ids: [secondIndicatorId],
            fields: ["idno", "name", "databases/idno"],
          };

          // datasetID is NOT available in these scenarios:
          // 1. initial page load — default second indicator from feature props
          // 2. second indicator comes from query param
          // so we need to fetch PortalMetadata  + AdminMetadata
          // after dispatch, selectedSecondIndicator.id changes, re-triggering this useEffect into the else part.
          if (!datasetID) {
            const [secondIndicatorResponse, adminMetadataResponse] =
              await Promise.allSettled([
                fetchData(
                  getEntitiesByIdEndpoint,
                  "POST",
                  getEntitiesByIdPayload,
                  secondIndicatorHeaders,
                ),
                fetchData(
                  adminMetadataEndpoint,
                  "POST",
                  adminMetadataPayload,
                  secondIndicatorHeaders,
                ),
              ]);

            let visualization = null;
            if (
              adminMetadataResponse?.value?.code === API_SUCCESS_CODE &&
              adminMetadataResponse?.value?.data?.[0]?.admin_metadata
            ) {
              visualization =
                adminMetadataResponse.value.data[0].admin_metadata?.[site]
                  ?.visualization;
            }

            if (
              secIndicatorQueryParam &&
              secIndicatorQueryParam.toLowerCase() !==
                secondIndicator.toLowerCase() &&
              secIndicatorQueryParam !== CORRELATION_NONE_INDICATOR_VALUE?.id
            ) {
              if (
                secondIndicatorResponse?.value?.code === API_SUCCESS_CODE &&
                secondIndicatorResponse?.value?.data?.[0]
              ) {
                dispatch(
                  setSelectedSecondIndicator({
                    id: secondIndicatorResponse?.value.data?.[0]?.idno,
                    name: secondIndicatorResponse?.value.data?.[0]?.name,
                    datasetID:
                      secondIndicatorResponse?.value.data?.[0]?.databases?.[0]
                        ?.idno,
                    visualization: visualization,
                    adminMetadataFetched: true,
                  }),
                );
              }
            } else if (secondIndicatorResponse?.value?.data?.[0]) {
              if (
                secIndicatorQueryParam === CORRELATION_NONE_INDICATOR_VALUE?.id
              ) {
                dispatch(
                  setSelectedSecondIndicator(CORRELATION_NONE_INDICATOR_VALUE),
                );
              } else {
                dispatch(
                  setSelectedSecondIndicator({
                    id: secondIndicatorResponse?.value.data?.[0]?.idno,
                    name: secondIndicatorResponse?.value.data?.[0]?.name,
                    datasetID:
                      secondIndicatorResponse?.value.data?.[0]?.databases?.[0]
                        ?.idno || "",
                    visualization: visualization,
                    adminMetadataFetched: true,
                  }),
                );
              }
            }
          } else {
            // datasetID is available in these scenarios:
            // 1. user selected a second indicator from the dropdown (provides id, name, datasetID)
            // 2. re-triggered run after the above !datasetID condition code dispatched metadata to Redux
            // dropdown doesn't provide visualization, so fetch admin metadata.
            if (
              !selectedSecondIndicator.adminMetadataFetched &&
              !selectedSecondIndicator?.visualization
            ) {
              try {
                const adminMetadataResponse = await fetchData(
                  adminMetadataEndpoint,
                  "POST",
                  adminMetadataPayload,
                  secondIndicatorHeaders,
                );
                if (
                  adminMetadataResponse?.code === API_SUCCESS_CODE &&
                  adminMetadataResponse?.data?.[0]?.admin_metadata
                ) {
                  const visualization =
                    adminMetadataResponse.data[0].admin_metadata?.[site]
                      ?.visualization;
                  dispatch(
                    setSelectedSecondIndicator({
                      ...selectedSecondIndicator,
                      visualization: visualization,
                    }),
                  );
                }
              } catch (error) {
                console.error(
                  "Error fetching admin metadata for second indicator:",
                  error,
                );
              }
            }

            // Fetch dimensions
            const payload = {
              database_id: datasetID,
              indicator_id: secondIndicatorId.trim().toUpperCase(),
              isLatestData: true,
            };

            const dimensionsEndpoint = getEndpointFromConfig(
              endpointList,
              endpointLabels.Dimensions,
            )?.apiEndpoint;

            const dimensionsResponse = await fetchData(
              dimensionsEndpoint,
              "POST",
              payload,
              headers,
            );

            dispatch(setSecondDisaggregationData(dimensionsResponse));
          }
        } else {
          dispatch(
            setSelectedSecondIndicator(CORRELATION_NONE_INDICATOR_VALUE),
          );
        }
      } catch (error) {
        console.error(
          "Error fetching second indicator metadata/dimensions:",
          error,
        );
      } finally {
        dispatch(setIsSecondIndicatorLoading(false));
      }
    };

    if (
      chartType === chartTypes.Correlation ||
      (getQueryParam(QueryParams.View) === chartTypes.Correlation &&
        getQueryParam(QueryParams.SecondIndicator) &&
        getQueryParam(QueryParams.SecondIndicator) !==
          CORRELATION_NONE_INDICATOR_VALUE?.id)
    ) {
      endpointList.length && fetchSecondIndicatorMetadataAndDimensions();
    }
  }, [chartType, selectedSecondIndicator?.id,endpointList]);

  useEffect(() => {
    const fetchSecondIndicatorData = async () => {
      try {
        dispatch(setIsSecondIndicatorLoading(true));

        const datasetID = selectedSecondIndicator?.datasetID;
        const secondIndicatorId =
          selectedSecondIndicator?.id?.trim()?.toUpperCase() || "";

        let params = `database_id=${datasetID}&indicator_id=${secondIndicatorId}`;
        let filters = [];

        if (isMultipleUnitMeasureExist(secondDisaggregationData)) {
          const defaultUnitMeasure = getDataByDimension(
            secondDisaggregationData?.dimensions,
            disaggregations.UnitMeasure,
          )[0].code;
          const secondUnitMeasureQueryParam = getQueryParam(
            QueryParams.SecondUnitMeasure,
          );
          filters.push({
            filterColumn: disaggregations.UnitMeasure,
            filterValue: secondUnitMeasureQueryParam
              ? secondUnitMeasureQueryParam
              : defaultUnitMeasure,
          });

          dispatch(
            setSelectedSecondUnitMeasure(
              secondUnitMeasureQueryParam
                ? secondUnitMeasureQueryParam
                : defaultUnitMeasure,
            ),
          );
          dispatch(setDefaultSecondUnitMeasure(defaultUnitMeasure));
        }

        const dimensions = secondDisaggregationData?.dimensions;
        dimensions?.forEach((item) => {
          const { field_name, field_value } = item;
          if (
            field_name &&
            field_name !== disaggregations.UnitMeasure &&
            field_name !== disaggregations.TimePeriod &&
            field_name !== disaggregations.RefArea &&
            field_name !== disaggregations.Region &&
            field_value?.length > 1
          ) {
            filters.push({
              filterColumn: field_name,
              filterValue: field_value[0]?.code,
            });
          }
        });

        if (recentYearSelection) {
          if (
            selectedSecondIndicator?.visualization?.temporal_default ===
            "latest_data"
          ) {
            const maxYear = getMaxYearFromDimensions(
              secondDisaggregationData?.dimensions,
            );
            if (maxYear) {
              params = `${params}&timePeriodFrom=${maxYear}&timePeriodTo=${maxYear}`;
            } else {
              params = `${params}&isLatestData=true`;
            }
          } else {
            params = `${params}&isLatestData=true`;
          }
        } else {
          params = `${params}&timePeriodFrom=${selectedSingleYear}&timePeriodTo=${selectedSingleYear}`;
        }

        const endpoint = `${
          getEndpointFromConfig(endpointList, endpointLabels.Data).apiEndpoint
        }?${params}${
          filters?.length ? `&filters=${formatFiltersForDataAPI(filters)}` : ""
        }`;
        const response = await dispatch(
          fetchAndSetData({
            endpoint,
            key: endpoint,
            headers: getAPIHeaders({ apiKeyName, apiKeyValue }),
          }),
        );
        dispatch(
          setSecondIndicatorData(response.payload ? response.payload : []),
        );
      } catch (error) {
        console.error("Error fetching second indicator data:", error);
      } finally {
        dispatch(setIsSecondIndicatorLoading(false));
      }
    };
    if (
      (chartType === chartTypes.Correlation ||
        getQueryParam(QueryParams.View) === chartTypes.Correlation) &&
      selectedSecondIndicator?.id &&
      selectedSecondIndicator.id !== CORRELATION_NONE_INDICATOR_VALUE?.id &&
      selectedSecondIndicator?.datasetID
    ) {
      fetchSecondIndicatorData();
    }
  }, [secondDisaggregationData, selectedSingleYear, chartType]);

  useEffect(() => {
    initializeChoices();
  }, [
    selectedCountries,
    selectedCountriesForBar,
    disaggregationCountries,
    isCountryListLoaded,
    chartType,
    selectedComparison,
    dimensionsData,
    boundaryType,
  ]);

  useEffect(() => {
    const initializeObserver = () => {
      const dropdown = document.querySelector(".choices");

      if (dropdown) {
        const observer = new MutationObserver(() => {
          const isActive = dropdown.classList.contains("is-open");
          setIsDropdownOpen(isActive);
        });

        observer.observe(dropdown, {
          attributes: true,
          attributeFilter: ["class"],
        });

        return () => {
          observer.disconnect();
        };
      }
    };

    // Delay the initialization to ensure the dropdown is rendered
    setTimeout(initializeObserver, 0);
  }, [document.querySelector(".choices__list")]);

  useEffect(() => {
    const elements = document.querySelectorAll(
      ".choices__list--multiple .choices__item",
    );
    if (chartType === chartTypes.Trend && selectedComparison === NONE) {
      let mapping = fetchCountryMappingForColor(
        regionCountriesMapping,
        selectedCountries,
      );

      elements?.forEach((element) => {
        const countryCode = element.getAttribute("data-value");
        const regionData = mapping.find((item) =>
          item.countries.includes(countryCode),
        );

        let badgeDot = element.querySelector(".badge-dot");
        if (!badgeDot) {
          badgeDot = document.createElement("span");
          badgeDot.classList.add("badge-dot");
          element.insertBefore(badgeDot, element.firstChild);
        }

        if (regionData) {
          const region = regionData.id;
          const countryIndex = regionData.countries.indexOf(countryCode);
          const color = COLOR_MAP[`${region}_countries`][countryIndex % 4];

          badgeDot.style.backgroundColor = color;
        }
      });
    } else if (chartType === chartTypes.Correlation) {
      elements?.forEach((element) => {
        const countryCode = element.getAttribute("data-value");
        const regionData = regionCountriesMapping.find((item) =>
          isCountryInGroup(item.countries, countryCode),
        );

        let badgeDot = element.querySelector(".badge-dot");
        if (!badgeDot) {
          badgeDot = document.createElement("span");
          badgeDot.classList.add("badge-dot");
          element.insertBefore(badgeDot, element.firstChild);
        }

        if (regionData) {
          const region = regionData.id;
          const color = COLOR_MAP[region];

          badgeDot.style.backgroundColor = color;
        }
      });
    } else {
      elements?.forEach((element) => {
        let badgeDot = element.querySelector(".badge-dot");
        if (!badgeDot) {
          badgeDot = document.createElement("span");
          badgeDot.classList.add("badge-dot");
          element.insertBefore(badgeDot, element.firstChild);
        }

        badgeDot.style.backgroundColor = COLOR_MAP.countryColorDefault;
      });
    }
  }, [
    isDropdownOpen,
    selectedCountries,
    selectedCountriesForBar,
    selectCountriesRef.current?.choices,
    regionCountriesMapping,
    selectCountriesRef.current?.choices?.dropdown?.isActive,
    dimensionsData,
  ]);

  useEffect(() => {
    const filterIndicatorData = () => {
      const filterKeys = Object.keys(selectedFilters);
      if (
        (chartType === chartTypes.Trend || chartType === chartTypes.Bar) &&
        selectedComparison !== NONE &&
        selectedFilters[selectedComparison] === ""
      ) {
        dispatch(setFilteredIndicatorData([]));
      } else if (
        chartType === chartTypes.Bar &&
        selectedCountriesForBar.length === 0
      ) {
        dispatch(setFilteredIndicatorData([]));
      } else if (
        chartType === chartTypes.DataTable &&
        filterKeys.some((k) => selectedFilters[k] === "")
      ) {
        dispatch(setFilteredIndicatorData([]));
      } else {
        const filteredData = indicatorData.filter((item) => {
          const checkFilter = (filter, value) => {
            if (!filter) return true;
            const filterValues = filter.split(",");
            return filterValues.includes(value);
          };

          const passesSelectedFilters = Object.entries(selectedFilters).every(
            ([filterKey, filterValue]) =>
              checkFilter(filterValue, item[filterKey]),
          );

          const passesFrequency =
            !selectedFrequency ||
            selectedFrequency
              .split(",")
              .includes(item?.[disaggregations.Frequency]);

          const passesUnitMeasure =
            !selectedUnitMeasure ||
            selectedUnitMeasure
              .split(",")
              .includes(item?.[disaggregations.UnitMeasure]);

          if (
            chartType === chartTypes.DataTable ||
            chartType === chartTypes.Map ||
            (chartType === chartTypes.Correlation &&
              selectedOnlyShow[chartType] === ONLY_SHOW_DEFAULT)
          ) {
            return (
              passesSelectedFilters && passesFrequency && passesUnitMeasure
            );
          } else if (
            chartType === chartTypes.Correlation &&
            selectedOnlyShow[chartType] !== ONLY_SHOW_DEFAULT
          ) {
            const regionsAndIncomeGroupsHierarchy = [
              ...(countryHierarchy?.REGION ?? []),
              ...(countryHierarchy?.INCOME ?? []),
            ];
            let countryList =
              regionsAndIncomeGroupsHierarchy
                .find((r) => r.id === selectedOnlyShow[chartType])
                ?.countries?.map((c) => c.id) || [];
            return (
              countryList.includes(item[disaggregations.RefArea]) &&
              passesSelectedFilters &&
              passesFrequency &&
              passesUnitMeasure
            );
          } else if (
            chartType === chartTypes.Bar &&
            // selectedCountriesForBar.length === 1 &&
            selectedCountriesForBar[0] === "all"
          ) {
            return (
              passesSelectedFilters && passesFrequency && passesUnitMeasure
            );
          } else if (
            chartType === chartTypes.Bar &&
            selectedCountriesForBar.length > 0 &&
            selectedCountriesForBar[0] !== "all"
          ) {
            return (
              selectedCountriesForBar.includes(item[disaggregations.RefArea]) &&
              passesSelectedFilters &&
              passesFrequency &&
              passesUnitMeasure
            );
          } else {
            return (
              selectedCountries.includes(item[disaggregations.RefArea]) &&
              passesSelectedFilters &&
              passesFrequency &&
              passesUnitMeasure
            );
          }
        });
        dispatch(setFilteredIndicatorData(filteredData));
      }
    };
    if (
      chartType === chartTypes.Correlation &&
      selectedOnlyShow[chartType] !== ONLY_SHOW_DEFAULT
    ) {
      if (countryHierarchy?.REGION?.length > 0) {
        filterIndicatorData();
      }
    } else {
      filterIndicatorData();
    }
  }, [
    chartType,
    indicatorData,
    selectedFilters,
    selectedCountries,
    selectedCountriesForBar,
    selectedUnitMeasure,
    selectedFrequency,
    selectedOnlyShow[chartType],
    countryHierarchy,
  ]);

  useEffect(() => {
    if (disaggregationData?.dimensions) {
      if (
        (chartType === chartTypes.Trend ||
          chartType === chartTypes.StackedBar ||
          ((chartType === chartTypes.DataTable ||
            chartType === chartTypes.Bar) &&
            !recentYearSelection)) &&
        selectedFrequency === periodicity.Daily
      ) {
        const timePeriodFilter = disaggregationData?.dimensions?.find(
          (item) => item.field_name === "TIME_PERIOD",
        );

        if (timePeriodFilter && timePeriodFilter.field_value.length > 0) {
          const dates = formatPeriodicityValues(
            periodicity.Daily,
            timePeriodFilter.field_value,
          );

          setDateList(dates);

          const initialMaxIndex = dates.length - 1;
          let initialMinIndex = initialMaxIndex - 31;

          if (initialMinIndex < 0) {
            initialMinIndex = 0;
          }

          setSelectedMinDateIndex(initialMinIndex);
          setSelectedMaxDateIndex(initialMaxIndex);

          dispatch(setSelectedMinYear(dates[initialMinIndex]));
          dispatch(setSelectedMaxYear(dates[initialMaxIndex]));

          setQueryParam(QueryParams.MinYear, dates[initialMinIndex]);
          setQueryParam(QueryParams.MaxYear, dates[initialMaxIndex]);
        }
      } else {
        if (selectedFrequency && selectedFrequency !== periodicity.Annual) {
          const timePeriodFilter = disaggregationData?.dimensions?.find(
            (item) => item.field_name === "TIME_PERIOD",
          );

          if (timePeriodFilter && timePeriodFilter.field_value.length > 0) {
            const result = formatPeriodicityValues(
              selectedFrequency,
              timePeriodFilter.field_value,
            );

            if (result.length > 0) {
              dispatch(setIndicatorMinYear(0));
              dispatch(setIndicatorMaxYear(result.length - 1));
              if (
                chartType === chartTypes.Bar ||
                chartType === chartTypes.DataTable ||
                chartType === chartTypes.Trend ||
                chartType === chartTypes.StackedBar
              ) {
                const minYearFromParam = getQueryParam(QueryParams.MinYear);
                const maxYearFromParam = getQueryParam(QueryParams.MaxYear);
                const finalMinYear =
                  minYearFromParam && result.includes(minYearFromParam)
                    ? minYearFromParam
                    : result?.[0];
                const finalMaxYear =
                  maxYearFromParam && result.includes(maxYearFromParam)
                    ? maxYearFromParam
                    : result?.[result.length - 1];
                setRangeYearList(result);
                dispatch(setSelectedMinYear(finalMinYear));
                dispatch(setSelectedMaxYear(finalMaxYear));
                setQueryParam(QueryParams.MinYear, finalMinYear);
                setQueryParam(QueryParams.MaxYear, finalMaxYear);
              } else {
                const singleYearFromParam = getQueryParam(QueryParams.Year);
                const finalSingleYear = singleYearFromParam
                  ? singleYearFromParam
                  : result?.[result.length - 1];
                dispatch(setSelectedSingleYear(finalSingleYear));
                setSingleRangeYearList(result);
              }
            }
          }
        } else {
          const timePeriodFilter = disaggregationData?.dimensions?.find(
            (item) => item.field_name === "TIME_PERIOD",
          );

          const minYearFromParams = getQueryParam(QueryParams.MinYear);
          const maxYearFromParams = getQueryParam(QueryParams.MaxYear);
          const singleYearFromParam = getQueryParam(QueryParams.Year);

          if (timePeriodFilter && timePeriodFilter.field_value.length > 0) {
            const years = timePeriodFilter.field_value
              .map((item) => parseInt(item.code))
              .sort((a, b) => a - b);

            const indMinYear = years?.[0];
            const indMaxYear = years?.[years.length - 1];

            dispatch(setIndicatorMinYear(indMinYear));
            dispatch(setIndicatorMaxYear(indMaxYear));

            const result = formatPeriodicityValues(
              periodicity.Annual,
              timePeriodFilter.field_value,
            );

            if (result.length > 0) {
              if (
                chartType === chartTypes.Bar ||
                chartType === chartTypes.DataTable ||
                chartType === chartTypes.Trend ||
                chartType === chartTypes.StackedBar
              ) {
                const finalMinYear =
                  minYearFromParams && result.includes(minYearFromParams)
                    ? minYearFromParams
                    : result?.[0];
                const finalMaxYear =
                  maxYearFromParams && result.includes(maxYearFromParams)
                    ? maxYearFromParams
                    : result[result.length - 1];
                setRangeYearList(result);
                dispatch(setSelectedMinYear(finalMinYear));
                dispatch(setSelectedMaxYear(finalMaxYear));
                setQueryParam(QueryParams.MinYear, finalMinYear);
                setQueryParam(QueryParams.MaxYear, finalMaxYear);
                const finalSingleYear =
                  singleYearFromParam && result.includes(singleYearFromParam)
                    ? singleYearFromParam
                    : result[result.length - 1];
                dispatch(setSelectedSingleYear(finalSingleYear));
              } else {
                const finalSingleYear =
                  singleYearFromParam && result.includes(singleYearFromParam)
                    ? singleYearFromParam
                    : result[result.length - 1];
                setSingleRangeYearList(result);
                dispatch(setSelectedSingleYear(finalSingleYear));
              }
            }
          }
        }
      }
    }
  }, [disaggregationData, selectedFrequency, chartType]);

  useEffect(() => {
    if (
      indicatorMetadata &&
      Object.keys(indicatorMetadata).length > 0 &&
      indicatorCode &&
      isCountryListLoaded &&
      isSelectedFiltersUpdated
    ) {
      if (isInitialDimensionsLoad.current) {
        isInitialDimensionsLoad.current = false;
        return;
      }

      // For Map chart, no need to pass REF_AREA filter at all
      // For Bar chart with 'all' selected, no need to pass REF_AREA filter
      const isBarWithAllSelected =
        chartType === chartTypes.Bar && selectedCountriesForBar.includes("all");

      if (chartType === chartTypes.Map || isBarWithAllSelected) {
        // Remove REF_AREA from dimensions list and call API
        const updatedDimensionsList = selectedDimensionsList.filter(
          (f) => f.filterColumn !== disaggregations.RefArea,
        );
        callDimensionsAPIImmediate(null, null, updatedDimensionsList);
        return;
      }

      const countriesForRefAreaRaw =
        chartType === chartTypes.Bar
          ? selectedCountriesForBar
          : selectedCountries;

      const countriesForRefArea = (
        Array.isArray(countriesForRefAreaRaw) ? countriesForRefAreaRaw : []
      ).filter((c) => c !== "all");

      const shouldForceSingleCountry =
        chartType === chartTypes.Pie ||
        chartType === chartTypes.StackedBar ||
        (chartType === chartTypes.Trend && selectedComparison !== NONE);

      const effectiveCountries = shouldForceSingleCountry
        ? countriesForRefArea.length > 0
          ? [countriesForRefArea[0]]
          : []
        : countriesForRefArea;

      let allRefAreas = [...effectiveCountries];

      const benchmarkList = [];
      let averages = clone(selectedAverages || []);
      averages = averages.filter(
        (item) =>
          item !== aggregates.Region.id && item !== aggregates.IncomeGroup.id,
      );

      if ((selectedAverages || []).includes(aggregates.Region.id)) {
        const regionCountriesMapping = countryHierarchy?.REGION || [];
        const regionList =
          effectiveCountries.length > 0
            ? getMappingForBenchmark(effectiveCountries, regionCountriesMapping)
            : regionCountriesMapping?.filter((region) =>
                disaggregationCountries?.find(
                  (country) => country.code === region.id,
                ),
              );
        benchmarkList.push(...regionList.map((item) => item.id));
      }

      if ((selectedAverages || []).includes(aggregates.IncomeGroup.id)) {
        const incomeGrpCountriesMapping = countryHierarchy?.INCOME || [];
        const incomeGroupList =
          effectiveCountries.length > 0
            ? getMappingForBenchmark(
                effectiveCountries,
                incomeGrpCountriesMapping,
              )
            : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                disaggregationCountries?.find(
                  (country) => country.code === incomeGroup.id,
                ),
              );
        benchmarkList.push(...incomeGroupList.map((item) => item.id));
      }

      allRefAreas = [
        ...new Set([...benchmarkList, ...averages, ...effectiveCountries]),
      ];

      const updatedDimensionsList = selectedDimensionsList.filter(
        (f) => f.filterColumn !== disaggregations.RefArea,
      );
      if (allRefAreas.length > 0) {
        updatedDimensionsList.push({
          filterColumn: disaggregations.RefArea,
          filterValue: allRefAreas.join(","),
        });
      }

      callDimensionsAPIImmediate(null, null, updatedDimensionsList);
    }
  }, [
    chartType,
    indicatorCode,
    indicatorMetadata,
    isCountryListLoaded,
    isSelectedFiltersUpdated,
  ]);
  // Cleanup debounce timers on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (dimensionsDebounceTimer.current) {
        clearTimeout(dimensionsDebounceTimer.current);
      }
    };
  }, []);

  const fetchIndicatorsData = async () => {
    if (indicatorMetadata && Object.keys(indicatorMetadata).length > 0) {
      try {
        dispatch(setIsLoading(true));

        let params = `database_id=${
          indicatorMetadata?.series_description?.database_id
        }&indicator_id=${indicatorCode.trim().toUpperCase()}`;

        let filters = [];
        if (Object.keys(selectedFilters)?.length > 0) {
          for (const [keyName, value] of Object.entries(selectedFilters)) {
            if (keyName && value) {
              filters.push({ filterColumn: keyName, filterValue: value });
            }
          }
        }

        if (
          chartType === chartTypes.Trend ||
          chartType === chartTypes.StackedBar
        ) {
          filters.push({
            filterColumn: disaggregations.RefArea,
            filterValue: selectedCountries?.join(","),
          });
          let countriesKey = clone(selectedCountries);
          countriesKey.sort((a, b) =>
            a.toUpperCase().localeCompare(b.toUpperCase()),
          );
          if (selectedFrequency === periodicity.Daily) {
            const minDateString = dateList[selectedMinDateIndex];
            const maxDateString = dateList[selectedMaxDateIndex];

            params = `${params}&timePeriodFrom=${minDateString}&timePeriodTo=${maxDateString}`;
          }
        }
        // else if (
        //   chartType === chartTypes.DataTable &&
        //   !recentYearSelection &&
        //   selectedFrequency === periodicity.Daily
        // ) {
        //   const minDateString = dateList[selectedMinDateIndex];
        //   const maxDateString = dateList[selectedMaxDateIndex];

        //   params = `${params}&timePeriodFrom=${minDateString}&timePeriodTo=${maxDateString}`;
        // }
        else if (
          chartType === chartTypes.Bar &&
          selectedCountriesForBar.length > 0 &&
          selectedCountriesForBar[0] !== "all"
        ) {
          filters.push({
            filterColumn: disaggregations.RefArea,
            filterValue: selectedCountriesForBar?.join(","),
          });
          let countriesKey = clone(selectedCountriesForBar);
          countriesKey.sort((a, b) =>
            a.toUpperCase().localeCompare(b.toUpperCase()),
          );
          if (selectedFrequency === periodicity.Daily) {
            const minDateString = dateList[selectedMinDateIndex];
            const maxDateString = dateList[selectedMaxDateIndex];

            params = `${params}&timePeriodFrom=${minDateString}&timePeriodTo=${maxDateString}`;
          }
        } else if (
          chartType === chartTypes.Pie &&
          selectedCountries.length > 0
        ) {
          filters.push({
            filterColumn: disaggregations.RefArea,
            filterValue: selectedCountries?.join(","),
          });
        }
        // Check for multiple frequency existence and pass frequency irrespective of chart type and Periodicity
        // Left previous conditions commented for future reference if needed
        if (
          isMultipleFrequencyExist(disaggregationData?.dimensions) &&
          selectedFrequency
          // chartType !== chartTypes.Trend &&
          // chartType !== chartTypes.StackedBar &&
          // && selectedFrequency !== periodicity.Annual
        ) {
          filters.push({
            filterColumn: disaggregations.Frequency,
            filterValue: selectedFrequency,
          });
        }
        if (
          chartType !== chartTypes.Trend &&
          chartType !== chartTypes.StackedBar
        ) {
          if (
            indicatorMetadata?.admin_metadata?.[site]?.visualization
              ?.temporal_default === "latest_data" &&
            recentYearSelection
          ) {
            params += `&timePeriodFrom=${indicatorMaxYear}&timePeriodTo=${indicatorMaxYear}`;
          } else if (recentYearSelection) {
            params += "&isLatestData=true";
          } else {
            params +=
              chartType === chartTypes.Bar || chartType === chartTypes.DataTable
                ? `&timePeriodFrom=${selectedMinYear}&timePeriodTo=${selectedMaxYear}`
                : `&timePeriodFrom=${selectedSingleYear}&timePeriodTo=${selectedSingleYear}`;
          }
        }

        const fullEndpoint = `${
          getEndpointFromConfig(endpointList, endpointLabels.Data).apiEndpoint
        }?${params}${
          filters?.length ? `&filters=${formatFiltersForDataAPI(filters)}` : ""
        }`;
        if (fullEndpoint.length > 2048) {
          const payload = {
            database_id: indicatorMetadata?.series_description?.database_id,
            indicator_id: indicatorCode.trim().toUpperCase(),
            isLatestData:
              chartType !== chartTypes.Trend &&
              chartType !== chartTypes.StackedBar &&
              indicatorMetadata?.additional?.visualization?.temporal_default !==
                "latest_data" &&
              recentYearSelection
                ? true
                : false,
            timePeriodFrom:
              chartType !== chartTypes.Trend &&
              chartType !== chartTypes.StackedBar &&
              indicatorMetadata?.additional?.visualization?.temporal_default ===
                "latest_data" &&
              !recentYearSelection
                ? selectedSingleYear
                : indicatorMetadata?.additional?.visualization
                      ?.temporal_default === "latest_data"
                  ? indicatorMaxYear
                  : "",
            timePeriodTo:
              chartType !== chartTypes.Trend &&
              chartType !== chartTypes.StackedBar &&
              indicatorMetadata?.additional?.visualization?.temporal_default ===
                "latest_data" &&
              !recentYearSelection
                ? selectedSingleYear
                : indicatorMetadata?.additional?.visualization
                      ?.temporal_default === "latest_data"
                  ? indicatorMaxYear
                  : "",
          };
          let filters = [];

          if (selectedFilters) {
            Object.entries(selectedFilters).forEach(([keyName, value]) => {
              if (value) {
                filters.push({ filterColumn: keyName, filterValue: value });
              }
            });
          }

          const refArea =
            chartType === chartTypes.Trend ||
            chartType === chartTypes.StackedBar
              ? selectedCountries?.join(",")
              : chartType === chartTypes.Bar &&
                  selectedCountriesForBar.length > 0 &&
                  selectedCountriesForBar[0] !== "all"
                ? selectedCountriesForBar?.join(",")
                : chartType === chartTypes.Pie && selectedCountries.length > 0
                  ? selectedCountries?.join(",")
                  : "";
          if (refArea) {
            filters.push({
              filterColumn: disaggregations.RefArea,
              filterValue: refArea,
            });
          }
          const frequency =
            chartType !== chartTypes.Trend &&
            chartType !== chartTypes.StackedBar &&
            selectedFrequency &&
            selectedFrequency !== periodicity.Annual
              ? selectedFrequency
              : "";
          if (frequency) {
            filters.push({
              filterColumn: disaggregations.Frequency,
              filterValue: frequency,
            });
          }
          payload.filters = filters;
          if (
            (chartType === chartTypes.Trend ||
              chartType === chartTypes.StackedBar ||
              chartType === chartTypes.DataTable ||
              chartType === chartTypes.Bar) &&
            selectedFrequency === periodicity.Daily
          ) {
            const minDateString = dateList[selectedMinDateIndex];
            const maxDateString = dateList[selectedMaxDateIndex];
            payload.timePeriodFrom = minDateString;
            payload.timePeriodTo = maxDateString;
          }
          const response = await dispatch(
            fetchAndSetPostMethodData({
              endpointData: {
                endpoint: getEndpointFromConfig(
                  endpointList,
                  endpointLabels.Data,
                ).apiEndpoint,
                method: "POST",
                data: payload,
                headers: getAPIHeaders({
                  apiKeyName,
                  apiKeyValue,
                }),
              },
              key: `post:${
                getEndpointFromConfig(endpointList, endpointLabels.Data)
                  .apiEndpoint
              }?${params}`,
            }),
          );
          dispatch(setIndicatorData(response.payload || []));
        } else {
          const response = await dispatch(
            fetchAndSetData({
              endpoint: fullEndpoint,
              key: fullEndpoint,
              headers: getAPIHeaders({
                apiKeyName,
                apiKeyValue,
              }),
            }),
          );
          dispatch(setIndicatorData(response.payload || []));
        }
        // TO-DO - need to set setSingleRangeYearList once api team added separate time periods for each frequency in disaggregation
      } catch (error) {
        dispatch(setIsLoading(false));
        console.error("Error fetching indicator data:", error);
      } finally {
        dispatch(setIsLoading(false));
      }
    }
  };
  const getValidFiltersForPayload = (filters, availableDimensions) => {
    return filters?.map((filter) => {
      const availableValues = availableDimensions[filter?.filterColumn];
      if (availableValues && availableValues.length > 0) {
        const currentValues = filter.filterValue
          ? filter.filterValue.split(",")
          : [];
        const validValues = currentValues.filter((val) =>
          availableValues.includes(val),
        );

        if (validValues.length === 0) {
          return {
            ...filter,
            filterValue: availableValues[0],
          };
        }
      }
      return filter;
    });
  };

  const ensureValidUnitAndFreqInPayload = (
    filters,
    availableDimensions,
    unitMeasure,
    frequency,
  ) => {
    const updatedFilters = [...filters];
    [disaggregations.UnitMeasure, disaggregations.Frequency].forEach((dim) => {
      const availableValues = availableDimensions[dim];
      const selectedValue =
        dim === disaggregations.UnitMeasure ? unitMeasure : frequency;

      if (availableValues && availableValues.length > 0 && selectedValue) {
        const filterIndex = updatedFilters.findIndex(
          (f) => f.filterColumn === dim,
        );

        if (!availableValues.includes(selectedValue)) {
          if (filterIndex !== -1) {
            updatedFilters[filterIndex].filterValue = availableValues[0];
          } else {
            updatedFilters.push({
              filterColumn: dim,
              filterValue: availableValues[0],
            });
          }
        } else {
          if (filterIndex === -1) {
            updatedFilters.push({
              filterColumn: dim,
              filterValue: selectedValue,
            });
          }
        }
      }
    });
    return updatedFilters;
  };

  const callDimensionsAPI = async (
    changedFilter = null,
    changedValue = null,
    updatedDimensionsList = null,
  ) => {
    if (dimensionsDebounceTimer.current) {
      clearTimeout(dimensionsDebounceTimer.current);
    }
    return new Promise((resolve) => {
      dimensionsDebounceTimer.current = setTimeout(async () => {
        const result = await executeDimensionsAPICall(
          changedFilter,
          changedValue,
          updatedDimensionsList,
        );
        resolve(result);
      }, 300);
    });
  };

  const callDimensionsAPIImmediate = async (
    changedFilter = null,
    changedValue = null,
    updatedDimensionsList = null,
  ) => {
    return await executeDimensionsAPICall(
      changedFilter,
      changedValue,
      updatedDimensionsList,
    );
  };

  const executeDimensionsAPICall = async (
    changedFilter = null,
    changedValue = null,
    updatedDimensionsList = null,
  ) => {
    let recentYearValue =
      chartType === chartTypes.Trend || chartType === chartTypes.StackedBar
        ? false
        : recentYearSelection;
    let timePeriodFrom = null;
    let timePeriodTo = null;
    if (changedFilter === "recentYearSelection") {
      recentYearValue = changedValue === "true" || changedValue === true;
    } else if (changedFilter === "TimePeriodFrom") {
      timePeriodFrom = `${changedValue}`;
      recentYearValue = false;
    } else if (changedFilter === "TimePeriodTo") {
      timePeriodTo = `${changedValue}`;
      recentYearValue = false;
    } else if (changedFilter === "TimePeriodRange") {
      const [fromValue, toValue] = changedValue.split(",");
      timePeriodFrom = `${fromValue}`;
      timePeriodTo = `${toValue}`;
      recentYearValue = false;
    } else if (changedFilter === "TimePeriodSingle") {
      timePeriodFrom = `${changedValue}`;
      timePeriodTo = `${changedValue}`;
      recentYearValue = false;
    }
    if (!indicatorMetadata || Object.keys(indicatorMetadata).length === 0) {
      return;
    }
    try {
      dispatch(setIsDimensionsLoading(true));
      const datasetID = indicatorMetadata?.series_description?.database_id;
      if (!datasetID) {
        dispatch(setIsDimensionsLoading(false));
        return;
      }
      let payload = {
        database_id: datasetID,
        indicator_id: indicatorCode.trim().toUpperCase(),
        timePeriodFrom: timePeriodFrom,
        timePeriodTo: timePeriodTo,
        isLatestData: recentYearValue,
        filters: [],
      };

      const dimensionsListToUse =
        updatedDimensionsList !== null
          ? updatedDimensionsList
          : selectedDimensionsList;
      if (dimensionsListToUse && dimensionsListToUse.length > 0) {
        payload.filters = dimensionsListToUse.map((filter) => ({
          filterColumn: filter.filterColumn,
          filterValue: filter.filterValue,
        }));
      }
      if (
        changedFilter &&
        !changedValue &&
        ![
          "recentYearSelection",
          "TimePeriodFrom",
          "TimePeriodTo",
          "TimePeriodRange",
          "TimePeriodSingle",
        ].includes(changedFilter)
      ) {
        // Remove the filter from payload when value is empty
        payload.filters = payload.filters.filter(
          (f) => f.filterColumn !== changedFilter,
        );
        // Remove from Redux store as well
        dispatch(removeFromSelectedDimensionsList(changedFilter));
      }

      if (
        changedFilter &&
        changedValue &&
        ![
          "recentYearSelection",
          "TimePeriodFrom",
          "TimePeriodTo",
          "TimePeriodRange",
          "TimePeriodSingle",
        ].includes(changedFilter)
      ) {
        const existingFilterIndex = payload.filters?.findIndex(
          (f) => f.filterColumn === changedFilter,
        );

        if (existingFilterIndex !== -1) {
          payload.filters[existingFilterIndex].filterValue = changedValue;
        } else {
          payload.filters.push({
            filterColumn: changedFilter,
            filterValue: changedValue,
          });
        }

        dispatch(
          setSelectedDimensionsList({
            filterName: changedFilter,
            filterValue: changedValue,
          }),
        );
      }
      if (
        chartType === chartTypes.Trend ||
        chartType === chartTypes.StackedBar
      ) {
        if (selectedFrequency === periodicity.Daily) {
          const minDateString =
            timePeriodFrom || dateList[selectedMinDateIndex];
          const maxDateString = timePeriodTo || dateList[selectedMaxDateIndex];
          payload.timePeriodFrom = minDateString;
          payload.timePeriodTo = maxDateString;
        } else {
          payload.timePeriodFrom =
            timePeriodFrom || selectedMinYear ? `${selectedMinYear}` : null;
          payload.timePeriodTo =
            timePeriodTo || selectedMaxYear ? `${selectedMaxYear}` : null;
        }
      } else if (
        chartType === chartTypes.DataTable &&
        !recentYearValue &&
        selectedFrequency === periodicity.Daily
      ) {
        const minDateString = timePeriodFrom || dateList[selectedMinDateIndex];
        const maxDateString = timePeriodTo || dateList[selectedMaxDateIndex];
        payload.timePeriodFrom = minDateString;
        payload.timePeriodTo = maxDateString;
      } else if (
        chartType !== chartTypes.Trend &&
        chartType !== chartTypes.StackedBar
      ) {
        if (
          indicatorMetadata?.admin_metadata?.[site]?.visualization
            ?.temporal_default === "latest_data" &&
          recentYearValue
        ) {
          const yearValue =
            timePeriodFrom || timePeriodTo || `${indicatorMaxYear}`;

          payload.timePeriodFrom = yearValue;
          payload.timePeriodTo = yearValue;
          payload.isLatestData = false;
        } else if (recentYearValue) {
          payload.isLatestData = true;
          payload.timePeriodFrom = null;
          payload.timePeriodTo = null;
        } else {
          payload.isLatestData = false;
          if (
            chartType === chartTypes.Bar ||
            chartType === chartTypes.DataTable
          ) {
            payload.timePeriodFrom = timePeriodFrom || `${selectedMinYear}`;
            payload.timePeriodTo = timePeriodTo || `${selectedMaxYear}`;
          } else {
            const yearValue =
              timePeriodFrom || timePeriodTo || `${selectedSingleYear}`;

            payload.timePeriodFrom = yearValue;
            payload.timePeriodTo = yearValue;
          }
        }
      }

      if (updatedDimensionsList !== null && payload.filters) {
        dispatch(replaceSelectedDimensionsList(payload.filters));
      }

      const endpoint = getEndpointFromConfig(
        endpointList,
        endpointLabels.Dimensions,
      ).apiEndpoint;
      const dimensionHeaders = getAPIHeaders({
        apiKeyName,
        apiKeyValue,
      });

      let response;
      let responseWithoutRefArea;
      const hasRefArea = payload.filters?.some(
        (f) => f.filterColumn === disaggregations.RefArea,
      );
      if (hasRefArea) {
        response = await fetchData(endpoint, "POST", payload, dimensionHeaders);

        let filtersForPayload = Object.keys(selectedFilters)
          .map((key) => {
            const payloadFilter = payload?.filters?.find(
              (f) => f.filterColumn === key,
            );
            return payloadFilter
              ? payloadFilter
              : {
                  filterColumn: key,
                  filterValue: selectedFilters[key],
                };
          })
          ?.filter(
            (f) => f.filterColumn !== disaggregations.RefArea && f.filterValue,
          );

        // If current filters are not available in response, update them to available one
        if (
          response?.dimensions_filtered &&
          response?.dimensions_filtered?.length > 0
        ) {
          const availableDimensions = {};
          response?.dimensions_filtered?.forEach((item) => {
            availableDimensions[item.field_name] =
              item.field_value?.map((field) => field?.code) || [];
          });

          filtersForPayload = getValidFiltersForPayload(
            filtersForPayload,
            availableDimensions,
          );

          filtersForPayload = ensureValidUnitAndFreqInPayload(
            filtersForPayload,
            availableDimensions,
            selectedUnitMeasure,
            selectedFrequency,
          );
        }

        const payloadWithoutRefArea = {
          ...payload,
          filters: filtersForPayload,
        };

        responseWithoutRefArea = await fetchData(
          endpoint,
          "POST",
          payloadWithoutRefArea,
          dimensionHeaders,
        );
      } else {
        response = await fetchData(endpoint, "POST", payload, dimensionHeaders);
        responseWithoutRefArea = response;
      }

      let availableFilters = null;
      if (
        response?.dimensions_filtered ||
        responseWithoutRefArea?.dimensions_filtered
      ) {
        if (
          (response?.dimensions_filtered &&
            response.dimensions_filtered.length > 0) ||
          (responseWithoutRefArea?.dimensions_filtered &&
            responseWithoutRefArea.dimensions_filtered.length > 0)
        ) {
          availableFilters = {};
          response?.dimensions_filtered?.forEach((item) => {
            availableFilters[item.field_name] = item.field_value?.map(
              (field) => field?.code,
            );
          });
          if (responseWithoutRefArea?.dimensions_filtered) {
            const refAreaItem = responseWithoutRefArea.dimensions_filtered.find(
              (item) => item.field_name === disaggregations.RefArea,
            );
            if (refAreaItem) {
              availableFilters[disaggregations.RefArea] =
                refAreaItem.field_value?.map((field) => field?.code);
            }
          }
          dispatch(
            setDimensionsData(
              changedFilter
                ? {
                    availableFilters: availableFilters,
                    changedFilter: changedFilter,
                  }
                : {
                    availableFilters: availableFilters,
                  },
            ),
          );
        } else {
          dispatch(
            setDimensionsData({
              availableFilters: {},
              changedFilter: changedFilter,
            }),
          );
        }
      } else {
        dispatch(
          setDimensionsData({
            availableFilters: null,
            changedFilter: null,
          }),
        );
        dispatch(clearSelectedDimensionsList());
      }
      dispatch(setIsDimensionsLoading(false));

      return availableFilters;
    } catch (error) {
      console.error("Error calling dimensions API:", error);
      dispatch(setIsDimensionsLoading(false));
      return null;
    }
  };
  const initializeChoices = () => {
    if (selectCountriesRef.current && countryListForIndicator?.length > 0) {
      if (selectCountriesRef.current.choices) {
        selectCountriesRef.current.choices.destroy();
      }

      selectCountriesRef.current.choices = new Choices(
        selectCountriesRef.current,
        {
          removeItemButton: true,
          placeholder: true,
          placeholderValue: "+",
          maxItemCount:
            chartType === chartTypes.Pie ||
            chartType === chartTypes.StackedBar ||
            (chartType === chartTypes.Trend && selectedComparison !== NONE) ||
            chartType === chartTypes.Map
              ? 1
              : undefined,
          maxItemText: (maxItemCount) => {
            return chartType === chartTypes.Pie ||
              chartType === chartTypes.StackedBar
              ? `Only ${maxItemCount} value can be added.`
              : chartType !== chartTypes.Map
                ? `Only ${maxItemCount} value can be added. Select Comparison By "None" to display multiple countries.`
                : `Only ${maxItemCount} value can be added.`;
          },
        },
      );
      if (chartType === chartTypes.Bar) {
        selectedCountriesForBar.forEach((countryCode) => {
          selectCountriesRef.current.choices.setChoiceByValue(countryCode);
        });
      } else {
        selectedCountries.forEach((countryCode) => {
          selectCountriesRef.current.choices.setChoiceByValue(countryCode);
        });
      }
    }

    return () => {
      if (selectCountriesRef?.current.choices) {
        selectCountriesRef?.current.choices.destroy();
      }
    };
  };

  const validateAndUpdateUnitMeasureAndFrequency = (dimensions) => {
    if (dimensions?.[disaggregations.UnitMeasure] && selectedUnitMeasure) {
      if (
        !dimensions?.[disaggregations.UnitMeasure].includes(selectedUnitMeasure)
      ) {
        const newUnitMeasure =
          dimensions?.[disaggregations.UnitMeasure][0] || selectedUnitMeasure;
        dispatch(setSelectedUnitMeasure(newUnitMeasure));
        setQueryParam(QueryParams.UnitMeasure, newUnitMeasure);

        const unitMeasureItem = getUnitMeasureItems(disaggregationData)?.find(
          (item) => item.code === newUnitMeasure,
        );
        const unitMeasureName =
          unitMeasureItem?.description || unitMeasureItem?.code;
        dispatch(setUnitMeasureName(unitMeasureName));
      }
    }

    if (dimensions?.[disaggregations.Frequency] && selectedFrequency) {
      if (
        !dimensions?.[disaggregations.Frequency].includes(selectedFrequency)
      ) {
        const newFrequency =
          dimensions?.[disaggregations.Frequency][0] || selectedFrequency;
        dispatch(setSelectedFrequency(newFrequency));
        setQueryParam(QueryParams.Frequency, newFrequency);
      }
    }
  };

  const validateAndUpdateFilters = (dimensions) => {
    const updatedFilters = { ...selectedFilters };
    let filtersChanged = false;

    Object.keys(updatedFilters).forEach((key) => {
      const availableValues = dimensions[key];
      const currentValue = updatedFilters[key];

      if (!availableValues || !currentValue) return;

      const currentValues = currentValue.split(",");
      const validValues = currentValues.filter((val) =>
        availableValues.includes(val),
      );

      if (validValues.length === 0 && availableValues.length > 0) {
        updatedFilters[key] = availableValues[0];
        filtersChanged = true;

        dispatch(
          setSelectedDimensionsList({
            filterName: key,
            filterValue: availableValues[0],
          }),
        );
      } else if (validValues.length !== currentValues.length) {
        updatedFilters[key] = validValues.join(",");
        filtersChanged = true;

        dispatch(
          setSelectedDimensionsList({
            filterName: key,
            filterValue: validValues.join(","),
          }),
        );
      }
    });

    if (filtersChanged) {
       for (const [key, value] of Object.entries(updatedFilters)) {
        setQueryParam(key, value)
      }
      dispatch(
        setSelectedFilters({
          name: "init",
          value: updatedFilters,
          selectedComparison,
        }),
      );
    }
  };

  const handleOptionSelect = (event) => {
    const selectedOptions = Array.from(
      event.target.selectedOptions,
      (option) => option.value,
    );
    if (chartType === chartTypes.Pie) {
      dispatch(setSelectedAverage({ chartType, average: NONE }));
      setQueryParam(QueryParams.Average, NONE);
    }

    const shouldCallDimensionsAPI = ![
      chartTypes.Map,
      chartTypes.Correlation,
      chartTypes.DataTable,
    ].includes(chartType);

    const buildRefAreasForDimensions = (countries = []) => {
      const allCountries = Array.isArray(countries) ? countries : [];

      const benchmarkList = [];
      let averages = clone(selectedAverages || []);
      averages = averages.filter(
        (item) =>
          item !== aggregates.Region.id && item !== aggregates.IncomeGroup.id,
      );

      if ((selectedAverages || []).includes(aggregates.Region.id)) {
        const regionCountriesMapping = countryHierarchy?.REGION || [];
        const regionList =
          allCountries.length > 0
            ? getMappingForBenchmark(allCountries, regionCountriesMapping)
            : regionCountriesMapping?.filter((region) =>
                disaggregationCountries?.find(
                  (country) => country.code === region.id,
                ),
              );
        benchmarkList.push(...regionList.map((item) => item.id));
      }

      if ((selectedAverages || []).includes(aggregates.IncomeGroup.id)) {
        const incomeGrpCountriesMapping = countryHierarchy?.INCOME || [];
        const incomeGroupList =
          allCountries.length > 0
            ? getMappingForBenchmark(allCountries, incomeGrpCountriesMapping)
            : incomeGrpCountriesMapping?.filter((incomeGroup) =>
                disaggregationCountries?.find(
                  (country) => country.code === incomeGroup.id,
                ),
              );
        benchmarkList.push(...incomeGroupList.map((item) => item.id));
      }

      const refAreas = new Set([
        ...benchmarkList,
        ...averages,
        ...allCountries,
      ]);
      return Array.from(refAreas);
    };

    if (chartType === chartTypes.Bar) {
      // bar chart country selection should allow combination of all and individual countries
      if (selectedOptions.includes("all")) {
        let countries = [...selectedOptions];
        countries = countries.filter((item) => item !== "all");
        dispatch(setSelectedCountriesForBar(["all", ...countries]));
        setQueryParam(
          QueryParams.CountrySelection,
          formatCountriesForQueryParams(["all", ...countries]),
        );

        if (shouldCallDimensionsAPI) {
          const allRefAreas = buildRefAreasForDimensions(countries);
          callDimensionsAPIImmediate(
            disaggregations.RefArea,
            allRefAreas.join(","),
          ).then((dimensions) => {
            if (dimensions) {
              validateAndUpdateFilters(dimensions);
              validateAndUpdateUnitMeasureAndFrequency(dimensions);
            }
          });
        }
      } else {
        dispatch(setSelectedCountriesForBar(selectedOptions));
        setQueryParam(
          QueryParams.CountrySelection,
          formatCountriesForQueryParams(selectedOptions),
        );

        if (shouldCallDimensionsAPI) {
          const allRefAreas = buildRefAreasForDimensions(selectedOptions);
          callDimensionsAPIImmediate(
            disaggregations.RefArea,
            allRefAreas.join(","),
          ).then((dimensions) => {
            if (dimensions) {
              validateAndUpdateFilters(dimensions);
              validateAndUpdateUnitMeasureAndFrequency(dimensions);
            }
          });
        }
      }
      dispatch(
        setSelectedCountries(selectedOptions.filter((item) => item !== "all")),
      );
    } else {
      dispatch(setSelectedCountries(selectedOptions));
      if (selectedCountriesForBar.includes("all")) {
        let countries = selectedCountriesForBar.filter(
          (item) => item === "all",
        );
        countries = [...countries, ...selectedOptions];
        dispatch(setSelectedCountriesForBar(countries));       
      } else {
        dispatch(setSelectedCountriesForBar(selectedOptions));
      }
      setQueryParam(
        QueryParams.CountrySelection,
        formatCountriesForQueryParams(selectedOptions),
      );

      if (shouldCallDimensionsAPI) {
        const allRefAreas = buildRefAreasForDimensions(selectedOptions);
        callDimensionsAPIImmediate(
          disaggregations.RefArea,
          allRefAreas.join(","),
        ).then((dimensions) => {
          if (dimensions) {
            validateAndUpdateFilters(dimensions);
            validateAndUpdateUnitMeasureAndFrequency(dimensions);
          }
        });
      }
    }
  };

  const onSliderTrackClick = async (event, sliderType) => {
    const track = event.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = event.clientX - trackRect.left;
    const trackLength = trackRect.right - trackRect.left;
    const clickPositionRatio = clickPosition / trackLength;

    if (sliderType === "double") {
      const totalItems = rangeYearList.length;
      const clickedIndex = Math.min(
        totalItems - 1,
        Math.max(0, Math.round(clickPositionRatio * (totalItems - 1))),
      );
      const clickedYearValue = rangeYearList[clickedIndex];

      if (chartType === chartTypes.Bar || chartType === chartTypes.DataTable) {
        if (recentYearSelection) {
          dispatch(setRecentYearSelection(false));
          setQueryParam(QueryParams.RecentYearSelection, false);
        }
      }

      const minIndex = Math.max(
        0,
        rangeYearList.indexOf(String(selectedMinYear)),
      );
      const maxIndex = Math.max(
        0,
        rangeYearList.indexOf(String(selectedMaxYear)),
      );

      if (
        Math.abs(clickedIndex - minIndex) <= Math.abs(clickedIndex - maxIndex)
      ) {
        dispatch(setSelectedMinYear(clickedYearValue));
        setQueryParam(QueryParams.MinYear, clickedYearValue);
        const dimensions = await callDimensionsAPI(
          "TimePeriodRange",
          `${clickedYearValue},${selectedMaxYear}`,
        );
        if (dimensions) {
          validateAndUpdateUnitMeasureAndFrequency(dimensions);
          validateAndUpdateFilters(dimensions);
        }
      } else {
        dispatch(setSelectedMaxYear(clickedYearValue));
        setQueryParam(QueryParams.MaxYear, clickedYearValue);
        const dimensions = await callDimensionsAPI(
          "TimePeriodRange",
          `${selectedMinYear},${clickedYearValue}`,
        );
        if (dimensions) {
          validateAndUpdateUnitMeasureAndFrequency(dimensions);
          validateAndUpdateFilters(dimensions);
        }
      }
    } else {
      const totalItems = singleRangeYearList.length;
      const clickedIndex = Math.min(
        totalItems - 1,
        Math.max(0, Math.round(clickPositionRatio * (totalItems - 1))),
      );
      const clickedYearValue = singleRangeYearList[clickedIndex];

      if (recentYearSelection) {
        dispatch(setRecentYearSelection(false));
        setQueryParam(QueryParams.RecentYearSelection, false);
      }
      dispatch(setSelectedSingleYear(clickedYearValue));
      setQueryParam(QueryParams.Year, clickedYearValue);
      const dimensions = await callDimensionsAPI(
        "TimePeriodSingle",
        clickedYearValue,
      );
      if (dimensions) {
        validateAndUpdateUnitMeasureAndFrequency(dimensions);
        validateAndUpdateFilters(dimensions);
      }
    }
  };

  const handleMinYearChange = async (event) => {
    if (
      (chartType === chartTypes.Bar || chartType === chartTypes.DataTable) &&
      recentYearSelection
    ) {
      dispatch(setRecentYearSelection(false));
      setQueryParam(QueryParams.RecentYearSelection, false);
    }
    const yearValue = rangeYearList[event.target.value];
    dispatch(setSelectedMinYear(yearValue));
    setQueryParam(QueryParams.MinYear, yearValue);
    if (
      !(
        (chartType === chartTypes.Bar || chartType === chartTypes.DataTable) &&
        recentYearSelection
      )
    ) {
      if (
        chartType === chartTypes.Bar ||
        chartType === chartTypes.DataTable ||
        chartType === chartTypes.Trend ||
        chartType === chartTypes.StackedBar
      ) {
        const dimensions = await callDimensionsAPI(
          "TimePeriodRange",
          `${yearValue},${selectedMaxYear}`,
        );
        if (dimensions) {
          validateAndUpdateUnitMeasureAndFrequency(dimensions);
          validateAndUpdateFilters(dimensions);
        }
      } else {
        const dimensions = await callDimensionsAPI(
          "TimePeriodSingle",
          yearValue,
        );
        if (dimensions) {
          validateAndUpdateUnitMeasureAndFrequency(dimensions);
          validateAndUpdateFilters(dimensions);
        }
      }
    }
  };
  const handleMaxYearChange = async (event) => {
    if (
      (chartType === chartTypes.Bar || chartType === chartTypes.DataTable) &&
      recentYearSelection
    ) {
      dispatch(setRecentYearSelection(false));
      setQueryParam(QueryParams.RecentYearSelection, false);
    }
    const yearValue = rangeYearList[event.target.value];
    dispatch(setSelectedMaxYear(yearValue));
    setQueryParam(QueryParams.MaxYear, yearValue);
    if (
      !(
        (chartType === chartTypes.Bar || chartType === chartTypes.DataTable) &&
        recentYearSelection
      )
    ) {
      if (
        chartType === chartTypes.Bar ||
        chartType === chartTypes.DataTable ||
        chartType === chartTypes.Trend ||
        chartType === chartTypes.StackedBar
      ) {
        const dimensions = await callDimensionsAPI(
          "TimePeriodRange",
          `${selectedMinYear},${yearValue}`,
        );
        if (dimensions) {
          validateAndUpdateUnitMeasureAndFrequency(dimensions);
          validateAndUpdateFilters(dimensions);
        }
      } else {
        const dimensions = await callDimensionsAPI(
          "TimePeriodSingle",
          yearValue,
        );
        if (dimensions) {
          validateAndUpdateUnitMeasureAndFrequency(dimensions);
          validateAndUpdateFilters(dimensions);
        }
      }
    }
  };
  const handleSingleYearChange = async (event) => {
    if (recentYearSelection) {
      dispatch(setRecentYearSelection(false));
      setQueryParam(QueryParams.RecentYearSelection, false);
    }
    const yearValue = singleRangeYearList[event.target.value];
    dispatch(setSelectedSingleYear(yearValue));
    setQueryParam(QueryParams.Year, yearValue);
    if (!recentYearSelection) {
      const dimensions = await callDimensionsAPI("TimePeriodSingle", yearValue);
      if (dimensions) {
        validateAndUpdateUnitMeasureAndFrequency(dimensions);
        validateAndUpdateFilters(dimensions);
      }
    }
  };
  const handleRecentYearCheckboxChange = async (event) => {
    const isChecked = event.target.checked;
    dispatch(setRecentYearSelection(isChecked));
    setQueryParam(QueryParams.RecentYearSelection, isChecked);
    const dimensions = await callDimensionsAPI(
      "recentYearSelection",
      isChecked.toString(),
    );
    if (dimensions) {
      validateAndUpdateUnitMeasureAndFrequency(dimensions);
      validateAndUpdateFilters(dimensions);
    }
  };

  const setRecentYear = () => {
    let mostRecentYear = null;
    if (filteredIndicatorData?.length > 0 && filteredIndicatorData?.[0]?.data) {
      for (
        let i = filteredIndicatorData?.[0]?.data?.xAxis?.length - 1;
        i >= 0;
        i--
      ) {
        const yearValue = filteredIndicatorData?.[0]?.data?.yAxis?.[i];
        if (yearValue !== null) {
          mostRecentYear = filteredIndicatorData?.[0]?.data?.xAxis?.[i];
          break;
        }
      }
    }
    if (mostRecentYear) {
      dispatch(setSelectedSingleYear(mostRecentYear));
    }
  };

  const resetFilters = async () => {
    dispatch(setIsLoading(true));
    dispatch(setSelectedCountriesForBar(["all"]));
    dispatch(resetSelectedCountries());

    dispatch(setSelectedComparison(NONE));
    const initialFilters = {};
    fetchDisaggregationList(disaggregationData?.dimensions).forEach(
      (filter) => {
        if (filter.field_value?.length > 0) {
          const defaultOption = filter.field_value[0];
          initialFilters[filter.field_name] = defaultOption.code;
        }
      },
    );
    dispatch(setInitialFilters(initialFilters));
    dispatch(setSelectedMinYear(indicatorMinYear));
    dispatch(setSelectedMaxYear(indicatorMaxYear));
    dispatch(setSelectedSingleYear(indicatorMaxYear));
    dispatch(setRecentYearSelection(true));
    dispatch(
      setSelectedOnlyShow({
        chartType: [chartTypes.Map],
        selectedOnlyShow: ONLY_SHOW_DEFAULT,
      }),
    );
    dispatch(
      setSelectedOnlyShow({
        chartType: [chartTypes.Correlation],
        selectedOnlyShow: ONLY_SHOW_DEFAULT,
      }),
    );

    if (isSingleSelectionGrouping) {
      dispatch(resetSelectedAverage(NONE));
    } else {
      if (
        disaggregationCountries.length > 0 &&
        chartType === chartTypes.Trend
      ) {
        if (
          disaggregationCountries
            .map((item) => item.code)
            .includes(WORLD_DEFAULT?.id)
        ) {
          dispatch(setSelectedAverages([WORLD_DEFAULT?.id]));
        } else if (
          disaggregationCountries.map((item) => item.code).includes(TOTAL)
        ) {
          dispatch(setSelectedAverages([TOTAL]));
        } else {
          dispatch(setSelectedAverages([]));
        }
      } else {
        dispatch(setSelectedAverages([]));
      }
    }
    try {
      const secondIndicatorHeaders = getAPIHeaders({
        isInternalPage,
        apiKeyName,
        apiKeyValue,
      });

      const defaultIndicatorResponse = await fetchData(
        `${
          getEndpointFromConfig(endpointList, endpointLabels.PortalMetadata)
            .apiEndpoint
        }?type=indicator&id=${secondIndicator}`,
        "GET",
        null,
        secondIndicatorHeaders,
      );
      if (
        defaultIndicatorResponse?.code === API_SUCCESS_CODE &&
        defaultIndicatorResponse?.data
      ) {
        dispatch(
          setSelectedSecondIndicator({
            id: defaultIndicatorResponse?.data?.series_description?.idno,
            name: defaultIndicatorResponse?.data?.series_description?.name,
            datasetID:
              defaultIndicatorResponse?.data?.series_description?.database_id ||
              "",
            visualization:
              defaultIndicatorResponse?.data?.admin_metadata?.[site]
                ?.visualization,
          }),
        );
      }
    } catch (error) {
      console.error(
        "Error fetching second indicator metadata on reset:",
        error,
      );
    }

    if (defaultFrequency) {
      dispatch(setSelectedFrequency(defaultFrequency));
    }
    if (defaultUnitMeasure) {
      dispatch(setSelectedUnitMeasure(defaultUnitMeasure));
    }
    if (defaultSecondUnitMeasure) {
      dispatch(setSelectedSecondUnitMeasure(defaultSecondUnitMeasure));
    }
    dispatch(setDimensionsData(null));
    dispatch(clearSelectedDimensionsList());
    deleteQueryParams(Object.values(QueryParams));
    const unitMeasureItem = getUnitMeasureItems(disaggregationData)?.find(
      (item) => item.code === defaultUnitMeasure,
    );
    const unitMeasureName =
      unitMeasureItem?.description || unitMeasureItem?.code;

    dispatch(setUnitMeasureName(unitMeasureName));
    dispatch(setIsLoading(false));
  };

  const handleUnitMeasureChange = async (event) => {
    const unitMeasure = event.target.value;

    const dimensions = await callDimensionsAPIImmediate(
      disaggregations.UnitMeasure,
      unitMeasure,
    );

    if (dimensions) {
      if (dimensions?.[disaggregations.Frequency] && selectedFrequency) {
        if (
          !dimensions?.[disaggregations.Frequency].includes(selectedFrequency)
        ) {
          const newFrequency =
            dimensions?.[disaggregations.Frequency][0] || selectedFrequency;
          dispatch(setSelectedFrequency(newFrequency));
          setQueryParam(QueryParams.Frequency, newFrequency);
        }
      }

      validateAndUpdateFilters(dimensions);
    }

    dispatch(setSelectedUnitMeasure(unitMeasure));
    setQueryParam(QueryParams.UnitMeasure, unitMeasure);

    const unitMeasureItem = getUnitMeasureItems(disaggregationData)?.find(
      (item) => item.code === unitMeasure,
    );
    const unitMeasureName =
      unitMeasureItem?.description || unitMeasureItem?.code;
    dispatch(setUnitMeasureName(unitMeasureName));
  };

  const handleSecondUnitMeasureChange = (event) => {
    const unitMeasure = event.target.value;
    dispatch(setSelectedSecondUnitMeasure(unitMeasure));
    setQueryParam(QueryParams.SecondUnitMeasure, unitMeasure);
    fetchDataOnUnitMeasureChange(unitMeasure);
  };

  const fetchDataOnUnitMeasureChange = async (unitMeasure) => {
    try {
      dispatch(setIsLoading(true));

      const datasetID = selectedSecondIndicator?.datasetID;
      let params = `database_id=${datasetID}&indicator_id=${selectedSecondIndicator?.id
        .trim()
        .toUpperCase()}`;
      const filters = [];
      fetchDisaggregationList(secondDisaggregationData?.dimensions).forEach(
        (filter) => {
          if (filter.field_value?.length > 0) {
            const value = filter.field_value[0];
            filters.push({
              filterColumn: filter.field_name,
              filterValue: value.code,
            });
          }
        },
      );

      filters.push({
        filterColumn: disaggregations.UnitMeasure,
        filterValue: unitMeasure,
      });

      const endpoint = `${
        getEndpointFromConfig(endpointList, endpointLabels.Data).apiEndpoint
      }?${params}${
        filters?.length ? `&filters=${formatFiltersForDataAPI(filters)}` : ""
      }`;
      const response = await dispatch(
        fetchAndSetData({
          endpoint,
          key: endpoint,
          headers: getAPIHeaders({ isInternalPage, apiKeyName, apiKeyValue }),
        }),
      );
      dispatch(
        setSecondIndicatorData(response.payload ? response.payload : []),
      );
    } catch (error) {
      console.error(
        "Error fetching second indicator data on unit measure change:",
        error,
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleFrequencyChange = async (event) => {
    const frequency = event.target.value;

    const dimensions = await callDimensionsAPIImmediate(
      disaggregations.Frequency,
      frequency,
    );

    if (dimensions) {
      if (dimensions?.[disaggregations.UnitMeasure] && selectedUnitMeasure) {
        if (
          !dimensions?.[disaggregations.UnitMeasure].includes(
            selectedUnitMeasure,
          )
        ) {
          const newUnitMeasure =
            dimensions?.[disaggregations.UnitMeasure][0] || selectedUnitMeasure;
          dispatch(setSelectedUnitMeasure(newUnitMeasure));
          setQueryParam(QueryParams.UnitMeasure, newUnitMeasure);

          const unitMeasureItem = getUnitMeasureItems(disaggregationData)?.find(
            (item) => item.code === newUnitMeasure,
          );
          const unitMeasureName =
            unitMeasureItem?.description || unitMeasureItem?.code;
          dispatch(setUnitMeasureName(unitMeasureName));
        }
      }

      validateAndUpdateFilters(dimensions);
    }

    dispatch(setSelectedFrequency(frequency));
    setQueryParam(QueryParams.Frequency, frequency);
  };

  const handleMinDateChange = async (event) => {
    const newIndex = parseInt(event.target.value);

    if (newIndex > selectedMaxDateIndex) {
      setSelectedMinDateIndex(selectedMaxDateIndex);
      setQueryParam(QueryParams.MinYear, dateList[selectedMaxDateIndex]);
      dispatch(setSelectedMinYear(dateList[selectedMaxDateIndex]));
    } else {
      const diffInDays = selectedMaxDateIndex - newIndex;

      if (diffInDays <= 31) {
        setSelectedMinDateIndex(newIndex);
        dispatch(setSelectedMinYear(dateList[newIndex]));
        setQueryParam(QueryParams.MinYear, dateList[newIndex]);
      } else {
        const adjustedMaxIndex = newIndex + 31;
        setSelectedMinDateIndex(newIndex);
        dispatch(setSelectedMinYear(dateList[newIndex]));
        setQueryParam(QueryParams.MinYear, dateList[newIndex]);
        setSelectedMaxDateIndex(
          adjustedMaxIndex < dateList.length
            ? adjustedMaxIndex
            : dateList.length - 1,
        );
        dispatch(
          setSelectedMaxYear(
            dateList[
              adjustedMaxIndex < dateList.length
                ? adjustedMaxIndex
                : dateList.length - 1
            ],
          ),
        );
        setQueryParam(
          QueryParams.MaxYear,
          dateList[
            adjustedMaxIndex < dateList.length
              ? adjustedMaxIndex
              : dateList.length - 1
          ],
        );
      }
    }
    const finalMinDateIndex =
      newIndex > selectedMaxDateIndex ? selectedMaxDateIndex : newIndex;
    const finalMaxDateIndex = selectedMaxDateIndex;
    const dimensions = await callDimensionsAPI(
      "TimePeriodRange",
      `${dateList[finalMinDateIndex]},${dateList[finalMaxDateIndex]}`,
    );
    if (dimensions) {
      validateAndUpdateUnitMeasureAndFrequency(dimensions);
      validateAndUpdateFilters(dimensions);
    }
  };

  const handleMaxDateChange = async (event) => {
    const newIndex = parseInt(event.target.value);
    const newDate = dateList[newIndex];

    const minDate = dateList[selectedMinDateIndex];
    const diffInDays = dateList.indexOf(newDate) - dateList.indexOf(minDate);

    if (newIndex < selectedMinDateIndex) {
      setSelectedMaxDateIndex(selectedMinDateIndex);
      setQueryParam(QueryParams.MaxYear, dateList[selectedMinDateIndex]);
      dispatch(setSelectedMaxYear(dateList[selectedMinDateIndex]));
    } else if (diffInDays <= 31) {
      setSelectedMaxDateIndex(newIndex);
      dispatch(setSelectedMaxYear(dateList[newIndex]));
      setQueryParam(QueryParams.MaxYear, dateList[newIndex]);
    } else {
      const adjustedMinIndex = dateList.findIndex(
        (date) => dateList.indexOf(newDate) - dateList.indexOf(date) <= 31,
      );
      setSelectedMinDateIndex(adjustedMinIndex);
      setSelectedMaxDateIndex(newIndex);

      dispatch(setSelectedMinYear(dateList[adjustedMinIndex]));
      dispatch(setSelectedMaxYear(dateList[newIndex]));

      setQueryParam(QueryParams.MinYear, dateList[adjustedMinIndex]);
      setQueryParam(QueryParams.MaxYear, dateList[newIndex]);
    }
    const finalMaxDateIndex =
      newIndex < selectedMinDateIndex ? selectedMinDateIndex : newIndex;
    const finalMinDateIndex = selectedMinDateIndex;
    const dimensions = await callDimensionsAPI(
      "TimePeriodRange",
      `${dateList[finalMinDateIndex]},${dateList[finalMaxDateIndex]}`,
    );
    if (dimensions) {
      validateAndUpdateUnitMeasureAndFrequency(dimensions);
      validateAndUpdateFilters(dimensions);
    }
  };

  const onDateSliderTrackClick = async (event) => {
    const track = event.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = event.clientX - trackRect.left;
    const trackLength = trackRect.right - trackRect.left;
    const clickPositionRatio = clickPosition / trackLength;
    const newIndex = Math.round(clickPositionRatio * (dateList.length - 1));

    if (newIndex < selectedMinDateIndex) {
      await handleMinDateChange({ target: { value: newIndex } });
    } else {
      await handleMaxDateChange({ target: { value: newIndex } });
    }
  };

  return (
    <div className="container responsivegrid">
      <div className="cmp-container">
        <div className="aem-Grid">
          <div className="tui__chart_tab__component aem-GridColumn--default--12 aem-GridColumn--desktop--12 aem-GridColumn--tablet--12 aem-GridColumn--phone--12">
            <div className="tui__clear_filters tui__btn lp__solid_lightblue_btn">
              <button
                data-customlink="nl:body content"
                className="tui__clear_filter_btn"
                aria-label="Reset all filters"
                onClick={() => resetFilters()}
              >
                <span className="lp lp-redo-alt"></span>
                <span>Reset Filters</span>
              </button>
            </div>

            {chartType === chartTypes.Correlation && (
              <SecondIndicatorSelection chartType={chartType} />
            )}

            {chartType === chartTypes.Map &&
              availableBoundaryTypes?.length > 1 && (
                <BoundaryTypeSelector chartType={chartType} />
              )}

            {countryListForIndicator?.length > 0 &&
              (chartType !== chartTypes.Map ||
                boundaryType === mapTypes.Country) && (
                <div className="tui__choice_container tui__label_text">
                  <span id="countryMultiselect" className="lp__dropdown_label">
                    {`Countries & Economies ${
                      (chartType === chartTypes.Trend ||
                        chartType === chartTypes.StackedBar) &&
                      selectedComparison !== NONE
                        ? "(Select 1)"
                        : chartType === chartTypes.Map ||
                            chartType === chartTypes.Correlation
                          ? "(Highlight)"
                          : ""
                    }`}
                  </span>

                  <div
                    className="multiselect-wrapper"
                    aria-labelledby="countryMultiselect"
                    key={`multiselect-${chartType}-${JSON.stringify(
                      dimensionsData,
                    )}`}
                  >
                    <select
                      id="choices-multiple-remove-button"
                      name="country-dropdown"
                      aria-label="country Multiselect"
                      multiple
                      ref={selectCountriesRef}
                      onChange={handleOptionSelect}
                    >
                      {chartType === chartTypes.Bar
                        ? [
                            { id: "all", name: "All" },
                            ...countryListForIndicator,
                          ].map((item) => (
                            <option
                              key={item.id}
                              value={item.id}
                              disabled={
                                dimensionsData &&
                                changedFilterName !== disaggregations.RefArea &&
                                dimensionsData[disaggregations.RefArea] &&
                                !dimensionsData[
                                  disaggregations.RefArea
                                ].includes(item.id)
                              }
                            >
                              {item.name || item.id}
                            </option>
                          ))
                        : countryListForIndicator.map((item) => (
                            <option
                              key={item.id}
                              value={item.id}
                              disabled={
                                dimensionsData &&
                                changedFilterName !== disaggregations.RefArea &&
                                dimensionsData[disaggregations.RefArea] &&
                                !dimensionsData[
                                  disaggregations.RefArea
                                ].includes(item.id)
                              }
                            >
                              {item.name || item.id}
                            </option>
                          ))}
                    </select>
                  </div>
                </div>
              )}

            {chartType === chartTypes.Correlation && (
              <OnlyShowDropdown chartType={chartType} />
            )}
            {(chartType !== chartTypes.Map ||
              boundaryType !== mapTypes.Country) && (
              <AverageDropdown
                chartType={chartType}
                callDimensionsAPIImmediate={callDimensionsAPIImmediate}
                validateAndUpdateFilters={validateAndUpdateFilters}
                validateAndUpdateUnitMeasureAndFrequency={
                  validateAndUpdateUnitMeasureAndFrequency
                }
              />
            )}
            {isMultipleUnitMeasureExist(disaggregationData) && (
              <div className="tui__label_text tui__secondary_dropdown tui__chart_dropdown tui_input_select">
                <label id={`${chartType}-unit-measure`}>Unit Measure</label>
                <select
                  htmlFor={`${chartType}-unit-measure`}
                  value={selectedUnitMeasure}
                  onChange={handleUnitMeasureChange}
                >
                  {chartType === chartTypes.Map ? (
                    <>
                      {getDataByDimension(
                        disaggregationData?.dimensions,
                        disaggregations.UnitMeasure,
                      )
                        ?.filter(
                          (item) =>
                            !featureProps?.excludedUnitMeasuresForMap
                              ?.split(",")
                              ?.includes(item.code),
                        )
                        .map((item) => (
                          <option
                            key={`unitmeasure-${item.code}`}
                            value={item.code}
                            className="ng-star-inserted"
                            disabled={
                              dimensionsData &&
                              changedFilterName !==
                                disaggregations.UnitMeasure &&
                              dimensionsData[disaggregations.UnitMeasure] &&
                              !dimensionsData[
                                disaggregations.UnitMeasure
                              ]?.includes(item.code)
                            }
                          >
                            {item.description || item.code}
                          </option>
                        ))}
                    </>
                  ) : (
                    <>
                      {getDataByDimension(
                        disaggregationData?.dimensions,
                        disaggregations.UnitMeasure,
                      )?.map((item) => (
                        <option
                          key={`unitmeasure-${item.code}`}
                          value={item.code}
                          className="ng-star-inserted"
                          disabled={
                            dimensionsData &&
                            dimensionsData[disaggregations.UnitMeasure] &&
                            changedFilterName !== disaggregations.UnitMeasure &&
                            !dimensionsData[
                              disaggregations.UnitMeasure
                            ]?.includes(item.code)
                          }
                        >
                          {item.description || item.code}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <span className="select-arrow"></span>
              </div>
            )}
            {chartType === chartTypes.Correlation &&
              selectedSecondIndicator &&
              selectedSecondIndicator.id !==
                CORRELATION_NONE_INDICATOR_VALUE?.id &&
              isMultipleUnitMeasureExist(secondDisaggregationData) && (
                <div className="tui__label_text tui__secondary_dropdown tui__chart_dropdown tui_input_select">
                  <label id={`${chartType}-second-unit-measure`}>
                    Unit Measure (Second Indicator)
                  </label>
                  <select
                    htmlFor={`${chartType}-second-unit-measure`}
                    value={selectedSecondUnitMeasure}
                    onChange={handleSecondUnitMeasureChange}
                  >
                    {getDataByDimension(
                      secondDisaggregationData?.dimensions,
                      disaggregations.UnitMeasure,
                    )?.map((item) => (
                      <option
                        key={`second-unitmeasure-${item.code}`}
                        value={item.code}
                        className="ng-star-inserted"
                      >
                        {item.description || item.code}
                      </option>
                    ))}
                  </select>
                  <span className="select-arrow"></span>
                </div>
              )}
            {isMultipleFrequencyExist(disaggregationData?.dimensions) && (
              <div className="tui__label_text tui__secondary_dropdown tui__chart_dropdown tui_input_select">
                <label id={`${chartType}-frequency`}>Frequency</label>
                <select
                  htmlFor={`${chartType}-frequency`}
                  value={selectedFrequency}
                  onChange={handleFrequencyChange}
                >
                  {getDataByDimension(
                    disaggregationData?.dimensions,
                    disaggregations.Frequency,
                  )?.map((item) => (
                    <option
                      key={`frequency-${item.code}`}
                      value={item.code}
                      className="ng-star-inserted"
                      disabled={
                        dimensionsData &&
                        changedFilterName !== disaggregations.Frequency &&
                        dimensionsData[disaggregations.Frequency] &&
                        !dimensionsData[disaggregations.Frequency]?.includes(
                          item.code,
                        )
                      }
                    >
                      {item.description || item.code}
                    </option>
                  ))}
                </select>
                <span className="select-arrow"></span>
              </div>
            )}
            <div className="tui__filter_overflow">
              <ComparisonByDisaggregation
                chartType={chartType}
                callDimensionsAPI={callDimensionsAPIImmediate}
              />
              {(indicatorMaxYear !== indicatorMinYear ||
                ((chartType === chartTypes.Trend ||
                  chartType === chartTypes.StackedBar ||
                  chartType === chartTypes.DataTable ||
                  chartType === chartTypes.Bar) &&
                  selectedFrequency === periodicity.Daily)) && (
                <>
                  {chartType === chartTypes.Trend ||
                  chartType === chartTypes.StackedBar ||
                  chartType === chartTypes.DataTable ||
                  chartType === chartTypes.Bar ? (
                    // ToDo- hide year slider for trend if selected countries or average are not there
                    selectedFrequency === periodicity.Daily &&
                    dateList.length > 0 ? (
                      <div className="tui__range_group">
                        <div className="range-wrapper">
                          <span className="tui_text_sm">Date Range</span>
                          {chartType === chartTypes.DataTable ||
                          chartType === chartTypes.Bar ? (
                            <>
                              <div className="tui__checkbox">
                                <input
                                  className="tui__primary_checkbox"
                                  type="checkbox"
                                  id="recentyearvalue"
                                  name="recentyearvalue"
                                  value="recentyearvalue"
                                  checked={recentYearSelection}
                                  onChange={(e) =>
                                    handleRecentYearCheckboxChange(e)
                                  }
                                />
                                <label htmlFor="recentyearvalue">
                                  <span>Show most recent values</span>
                                </label>
                              </div>
                              {!recentYearSelection && (
                                <>
                                  <div
                                    className="tooltip range_value_current range_value_current_daily_freq"
                                    style={{
                                      left: `${
                                        (selectedMinDateIndex /
                                          (dateList.length - 1)) *
                                        100
                                      }%`,
                                    }}
                                  >
                                    {dateList[selectedMinDateIndex]}
                                  </div>
                                  <div
                                    className="tooltip range_value_current range_value_current_daily_freq"
                                    style={{
                                      left: `${
                                        (selectedMaxDateIndex /
                                          (dateList.length - 1)) *
                                        100
                                      }%`,
                                    }}
                                  >
                                    {dateList[selectedMaxDateIndex]}
                                  </div>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <div
                                className="tooltip range_value_current range_value_current_daily_freq"
                                style={{
                                  left: `${
                                    (selectedMinDateIndex /
                                      (dateList.length - 1)) *
                                    100
                                  }%`,
                                }}
                              >
                                {dateList[selectedMinDateIndex]}
                              </div>
                              <div
                                className="tooltip range_value_current range_value_current_daily_freq"
                                style={{
                                  left: `${
                                    (selectedMaxDateIndex /
                                      (dateList.length - 1)) *
                                    100
                                  }%`,
                                }}
                              >
                                {dateList[selectedMaxDateIndex]}
                              </div>
                            </>
                          )}

                          <div className="input-range-wrapper">
                            <div
                              className="slider-track"
                              onClick={onDateSliderTrackClick}
                            ></div>
                            <input
                              className="range1"
                              type="range"
                              min={0}
                              max={dateList.length - 1}
                              value={selectedMinDateIndex}
                              onChange={handleMinDateChange}
                            />
                            <input
                              className="range2"
                              type="range"
                              min={0}
                              max={dateList.length - 1}
                              value={selectedMaxDateIndex}
                              onChange={handleMaxDateChange}
                            />
                          </div>
                          <div className="values">
                            <span className="range_min left">
                              {dateList[0]}
                            </span>
                            <span className="range_max right">
                              {dateList[dateList.length - 1]}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="tui__range_group">
                        <div className="range-wrapper">
                          <span className="tui_text_sm">Year</span>
                          {chartType === chartTypes.DataTable ||
                          chartType === chartTypes.Bar ? (
                            <>
                              <div className="tui__checkbox">
                                <input
                                  className="tui__primary_checkbox"
                                  type="checkbox"
                                  id="recentyearvalue"
                                  name="recentyearvalue"
                                  value="recentyearvalue"
                                  checked={recentYearSelection}
                                  onChange={(e) =>
                                    handleRecentYearCheckboxChange(e)
                                  }
                                />
                                <label htmlFor="recentyearvalue">
                                  <span>Show most recent values</span>
                                </label>
                              </div>
                              {String(selectedMaxYear) !==
                                rangeYearList[rangeYearList.length - 1] && (
                                <div
                                  className="tooltip range_value_current"
                                  style={{
                                    left: `${
                                      (Math.max(
                                        0,
                                        rangeYearList.indexOf(
                                          String(selectedMaxYear),
                                        ),
                                      ) /
                                        Math.max(rangeYearList.length - 1, 1)) *
                                      100
                                    }%`,
                                  }}
                                >
                                  {selectedMaxYear}
                                </div>
                              )}
                              {String(selectedMinYear) !== rangeYearList[0] && (
                                <div
                                  className="tooltip range_value_current"
                                  style={{
                                    left: `${
                                      (Math.max(
                                        0,
                                        rangeYearList.indexOf(
                                          String(selectedMinYear),
                                        ),
                                      ) /
                                        Math.max(rangeYearList.length - 1, 1)) *
                                      100
                                    }%`,
                                  }}
                                >
                                  {selectedMinYear}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {String(selectedMaxYear) !==
                                rangeYearList[rangeYearList.length - 1] && (
                                <div
                                  className="tooltip range_value_current"
                                  style={{
                                    left: `${
                                      (Math.max(
                                        0,
                                        rangeYearList.indexOf(
                                          String(selectedMaxYear),
                                        ),
                                      ) /
                                        Math.max(rangeYearList.length - 1, 1)) *
                                      100
                                    }%`,
                                  }}
                                >
                                  {selectedMaxYear}
                                </div>
                              )}
                              {String(selectedMinYear) !== rangeYearList[0] && (
                                <div
                                  className="tooltip range_value_current"
                                  style={{
                                    left: `${
                                      (Math.max(
                                        0,
                                        rangeYearList.indexOf(
                                          String(selectedMinYear),
                                        ),
                                      ) /
                                        Math.max(rangeYearList.length - 1, 1)) *
                                      100
                                    }%`,
                                  }}
                                >
                                  {selectedMinYear}
                                </div>
                              )}
                            </>
                          )}

                          <div className="input-range-wrapper">
                            <div
                              className="slider-track"
                              key="trend-slider"
                              onClick={(event) =>
                                onSliderTrackClick(event, "double")
                              }
                            ></div>
                            <input
                              className="range1"
                              type="range"
                              min={0}
                              max={rangeYearList.length - 1}
                              value={Math.max(
                                0,
                                rangeYearList.indexOf(String(selectedMinYear)),
                              )}
                              id="slider-1"
                              name="min-year"
                              aria-valuemin={selectedMinYear}
                              aria-label="minimum year"
                              onChange={handleMinYearChange}
                            />
                            <input
                              className="range2"
                              type="range"
                              min={0}
                              max={rangeYearList.length - 1}
                              value={Math.max(
                                0,
                                rangeYearList.indexOf(String(selectedMaxYear)),
                              )}
                              id="slider-2"
                              name="max-year"
                              aria-valuemax={selectedMaxYear}
                              aria-label="maximum year"
                              onChange={handleMaxYearChange}
                            />
                          </div>
                          <div className="values">
                            <span id="range1" className="range_min left">
                              {rangeYearList[0]}
                            </span>
                            <span id="range2" className="range_max right">
                              {rangeYearList[rangeYearList.length - 1]}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="tui__range_group">
                      <div className="range-wrapper">
                        <span className="tui_text_sm">Year</span>
                        <div className="tui__checkbox">
                          <input
                            className="tui__primary_checkbox"
                            type="checkbox"
                            id="recentyearvalue"
                            name="recentyearvalue"
                            value="recentyearvalue"
                            checked={recentYearSelection}
                            onChange={(e) => handleRecentYearCheckboxChange(e)}
                          />
                          <label htmlFor="recentyearvalue">
                            <span>Show most recent values</span>
                          </label>
                        </div>
                        {!recentYearSelection && (
                          <div
                            className="tooltip range_value_current"
                            style={{
                              left: `${
                                (Math.max(
                                  0,
                                  singleRangeYearList.indexOf(
                                    String(selectedSingleYear),
                                  ),
                                ) /
                                  Math.max(singleRangeYearList.length - 1, 1)) *
                                100
                              }%`,
                            }}
                          >
                            {selectedSingleYear}
                          </div>
                        )}

                        <div className="input-range-wrapper">
                          <div
                            className="slider-track"
                            key="single-slider"
                            onClick={(event) =>
                              onSliderTrackClick(event, "single")
                            }
                          ></div>

                          <input
                            className="range1"
                            type="range"
                            min={0}
                            max={singleRangeYearList.length - 1}
                            value={Math.max(
                              0,
                              singleRangeYearList.indexOf(
                                String(selectedSingleYear),
                              ),
                            )}
                            id="slider-1"
                            name="single-year"
                            aria-valuemin="0"
                            aria-label="minimum year"
                            onChange={handleSingleYearChange}
                          />
                        </div>
                        <div className="values">
                          <span id="range1" className="range_min left">
                            {singleRangeYearList[0]}
                          </span>
                          <span id="range2" className="range_max right">
                            {singleRangeYearList[singleRangeYearList.length - 1]}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Filters;
